<template>
  <div class="workpackage-selector">
    <v-autocomplete
      v-model="selectedIds"
      :items="wpItems"
      item-text="text"
      item-value="value"
      multiple
      chips
      small-chips
      deletable-chips
      :loading="loadingWp"
      :counter="maxSelections"
      :label="$t('general.labels.selectDefault')"
      :search-input.sync="search"
      :item-disabled="isItemDisabled"
      @blur="onBlur"
      @change="onChange"
    >
      <template v-slot:selection="{ item, index }">
        <v-chip v-if="index < 5" close @click:close="deselect(item.value)">{{ item.text }}</v-chip>
        <span v-else-if="index === 5">+{{ selectedIds.length - 5 }}</span>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import moment from 'moment';

export default {
  name: 'WorkpackageSelector',
  props: {
    value: {
      type: Array,
      required: true,
    },
    maxSelections: {
      type: Number,
      default: Infinity,
    },
  },
  data() {
    return {
      search: '',
    };
  },
  computed: {
    ...mapState('reporting', ['availableWorkpackages']),
    wpItems() {
      const s = (this.search || '').toLowerCase();
      const toItem = wp => ({
        text: `${wp.description} (${this.formatDate(wp.goLiveDate)})`,
        value: wp._id,
        goLiveDate: wp.goLiveDate,
      });
      return this.availableWorkpackages
        .filter(wp => wp.type !== 'master')
        .filter(wp => wp.description && wp.description.toLowerCase().includes(s))
        .sort((a, b) => {
          // Sort by go-live date descending (latest first)
          const dateA = a.goLiveDate ? new Date(a.goLiveDate) : new Date(0);
          const dateB = b.goLiveDate ? new Date(b.goLiveDate) : new Date(0);
          return dateB - dateA;
        })
        .map(toItem);
    },
    loadingWp() {
      const wpState = this.$store.state.workpackages || {};
      return !!(wpState.loading || wpState.inactiveLoading);
    },
    selectedIds: {
      get() {
        return this.value.map(wp => wp._id || wp);
      },
      set(ids) {
        const limitedIds = ids.length > this.maxSelections ? ids.slice(0, this.maxSelections) : ids;
        const mapById = new Map(this.availableWorkpackages.map(wp => [wp._id, wp]));
        const next = limitedIds.map(id => mapById.get(id)).filter(Boolean);
        this.$emit('input', next);
      },
    },
  },
  watch: {
    selectedIds(newIds) {
      if (Array.isArray(newIds) && newIds.length > this.maxSelections) {
        this.selectedIds = newIds.slice(0, this.maxSelections);
      }
    },
  },
  created() {
    this.fetchReportingWorkpackages({ type: 'workpackage' });
  },
  methods: {
    ...mapActions('reporting', ['fetchReportingWorkpackages']),
    formatDate(dateStr) {
      return dateStr ? moment(dateStr).format('YYYY-MM-DD') : '';
    },
    onChange(values) {
      if (values.length > this.maxSelections) {
        this.selectedIds = values.slice(0, this.maxSelections);
      }
      this.$emit('update:workpackages', this.value);
    },
    onBlur() {
      this.$emit('menu-closed');
    },
    isItemDisabled(item) {
      const isSelected = this.selectedIds.includes(item.value);
      return this.selectedIds.length >= this.maxSelections && !isSelected;
    },
    deselect(id) {
      this.selectedIds = this.selectedIds.filter(v => v !== id);
    },
  },
};
</script>

<style scoped></style>
